import { ApolloError } from "@apollo/client";
import { isJwtError } from "@dashboard/auth/errors";
import { GqlErrors } from "@dashboard/utils/api";
import { type GraphQLError } from "graphql";

/**
 * What became of one mutation the upload queue sent, told apart explicitly
 * rather than by `data === undefined`:
 *
 * - answered: the payload came back (it may still carry refusals in `errors`);
 * - refused: the API answered with a top-level GraphQL error (a permission,
 *   a bad argument): an answer, so never retried;
 * - noAnswer: nothing came back (offline, timeout, a 5xx from the edge), the
 *   one case a confirm is retried;
 * - halted: the session expired or the API is read-only. Every other file in
 *   the queue would get the same answer, so the queue stops.
 */
export type AtticAssetCallOutcome<TPayload> =
  | { kind: "answered"; payload: TPayload }
  | { kind: "refused"; message: string | null }
  | { kind: "noAnswer" }
  | { kind: "halted"; reason: AtticAssetHaltReason };

export type AtticAssetHaltReason = "sessionExpired" | "readOnly";

// An ApolloError carries them in `graphQLErrors`; a result read with
// errorPolicy "all" carries the plain list.
const graphQLErrorsOf = (error: unknown): readonly GraphQLError[] => {
  if (Array.isArray(error)) {
    return error as GraphQLError[];
  }

  if (error instanceof ApolloError || (error as ApolloError | null)?.graphQLErrors) {
    return (error as ApolloError).graphQLErrors ?? [];
  }

  return [];
};

/** A failed call: what an ApolloError (or anything thrown) says about the answer. */
export const getErrorCallOutcome = (error: unknown): AtticAssetCallOutcome<never> => {
  const graphQLErrors = graphQLErrorsOf(error);

  if (!graphQLErrors.length) {
    return { kind: "noAnswer" };
  }

  if (graphQLErrors.some(isJwtError)) {
    return { kind: "halted", reason: "sessionExpired" };
  }

  if (
    graphQLErrors.some(
      item =>
        (item.extensions?.exception as { code?: string } | undefined)?.code ===
        GqlErrors.ReadOnlyException,
    )
  ) {
    return { kind: "halted", reason: "readOnly" };
  }

  return { kind: "refused", message: graphQLErrors.find(item => item.message)?.message ?? null };
};

/**
 * One mutation's result as the Dashboard's mutation wrapper hands it back.
 * With an `onError` in place (the wrapper always passes one to Apollo), a
 * failed call resolves with `{ data: undefined, errors: ApolloError }` rather
 * than rejecting, so both shapes are read here.
 */
export const getCallOutcome = <TData, TPayload>(
  result: { data?: TData | null; errors?: unknown } | undefined,
  pick: (data: TData) => TPayload | null | undefined,
): AtticAssetCallOutcome<TPayload> => {
  if (result?.errors && (!Array.isArray(result.errors) || result.errors.length)) {
    return getErrorCallOutcome(result.errors);
  }

  const payload = result?.data ? pick(result.data) : null;

  // An answer with no payload at all is not one the API gives for these
  // mutations; read it as no answer rather than invent a refusal.
  return payload ? { kind: "answered", payload } : { kind: "noAnswer" };
};

/** Run one mutation and read its outcome; nothing it does escapes as a throw. */
export const callAtticAssetMutation = async <TData, TPayload>(
  run: () => Promise<{ data?: TData | null; errors?: unknown }>,
  pick: (data: TData) => TPayload | null | undefined,
): Promise<AtticAssetCallOutcome<TPayload>> => {
  try {
    return getCallOutcome(await run(), pick);
  } catch (error) {
    return getErrorCallOutcome(error);
  }
};
